import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  menuCategoryRequests,
  type CreateMenuCategoryInput,
  type ReorderMenuCategoriesInput,
} from '@/lib/api/requests/menu-categories';
import { menuItemKeys } from '../menu-items/keys';
import { menuCategoryKeys } from './keys';

/**
 * Renaming and deleting change what menu items show as their category, so those
 * refresh the item lists as well as the category list.
 */
function useRefresh() {
  const queryClient = useQueryClient();
  return {
    categories: () => queryClient.invalidateQueries({ queryKey: menuCategoryKeys.all }),
    items: () => {
      void queryClient.invalidateQueries({ queryKey: menuItemKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: menuItemKeys.details() });
    },
  };
}

export function useCreateMenuCategory() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (data: CreateMenuCategoryInput) => menuCategoryRequests.create(data),
    onSuccess: () => refresh.categories(),
  });
}

export function useRenameMenuCategory() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      menuCategoryRequests.rename(id, name),
    onSuccess: () => {
      void refresh.categories();
      refresh.items();
    },
  });
}

export function useReorderMenuCategories() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (data: ReorderMenuCategoriesInput) => menuCategoryRequests.reorder(data),
    // Settled, not success: after a failed save the list has to come back from
    // the server, or the dialog keeps showing an order that was never saved.
    onSettled: () => refresh.categories(),
  });
}

export function useDeleteMenuCategory() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, reassignToId }: { id: number; reassignToId?: number }) =>
      menuCategoryRequests.remove(id, reassignToId),
    onSuccess: () => {
      void refresh.categories();
      refresh.items();
    },
  });
}
